import { InternalAxiosRequestConfig } from 'axios';

import {
  getSessionExistsFromCookie,
  setSessionExistsInCookie,
} from './manageCookies';
import {
  getLoginToken,
  isTokenExpired,
  storeTokenExpiration,
} from '../getToken';
import {
  checkSessionExistsRaw,
  setTokenInCookies,
} from '../../../config/appwrite';

let tokenPromise: Promise<string | undefined> | null = null;
let sessionCheckPromise: Promise<boolean> | null = null;
let sessionExistsCache: boolean | null = null;

export const setAuthToken = async (
  config: InternalAxiosRequestConfig,
  isSchool = false,
  useBearer = true,
  isAskAi = false,
): Promise<InternalAxiosRequestConfig> => {
  try {
    config.headers = config.headers || {};

    let authToken = await getLoginToken();

    if (authToken && !isTokenExpired()) {
      if (!isAskAi) {
        config.headers['Authorization'] = useBearer
          ? `Bearer ${authToken}`
          : authToken;
      }
      return config;
    }

    const sessionExistsFromCookie = getSessionExistsFromCookie();

    if (sessionExistsFromCookie !== undefined) {
      if (!sessionExistsFromCookie) return config;

      if (!tokenPromise) {
        tokenPromise = setTokenInCookies();
      }

      authToken = await tokenPromise.finally(() => {
        tokenPromise = null;
      });

      if (authToken) {
        storeTokenExpiration();
        if (!isSchool && !isAskAi) {
          config.headers['Authorization'] = useBearer
            ? `Bearer ${authToken}`
            : authToken;
        }
      }

      return config;
    }

    if (sessionExistsCache === null) {
      if (!sessionCheckPromise) {
        sessionCheckPromise = checkSessionExistsRaw()
          .then((res) => {
            sessionExistsCache = !!res;
            // Persist the result so future API calls (and the next tab open
            // within the 2-hour window) skip this Appwrite network round-trip.
            setSessionExistsInCookie(sessionExistsCache);
            return sessionExistsCache;
          })
          .finally(() => {
            sessionCheckPromise = null;
          });
      }

      await sessionCheckPromise;
    }

    if (!sessionExistsCache) {
      // Cache negative result too so we don’t hit Appwrite on every request
      // when the user is logged out.
      setSessionExistsInCookie(false);
      return config;
    }

    if (!tokenPromise) {
      tokenPromise = setTokenInCookies();
    }

    authToken = await tokenPromise.finally(() => {
      tokenPromise = null;
    });

    if (authToken) {
      storeTokenExpiration();
      if (!isSchool && !isAskAi) {
        config.headers['Authorization'] = useBearer
          ? `Bearer ${authToken}`
          : authToken;
      }
    }

    return config;
  } catch (error) {
    console.error('Error setting auth token:', error);
    return config;
  }
};

export const getAuthTokenFromCookie = (
  cookieName: string,
): string | undefined => {
  try {
    if (typeof document === 'undefined') return undefined;

    const tokenCookie = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith(`${cookieName}=`));

    if (!tokenCookie) return undefined;

    const token = tokenCookie.split('=')[1];

    if (isTokenExpired()) {
      clearAuthToken(cookieName);
      return undefined;
    }

    return token;
  } catch {
    return undefined;
  }
};

export const clearAuthToken = (cookieName: string) => {
  document.cookie = `${cookieName}=; max-age=0; path=/`;
  localStorage.removeItem('auth_token_expiration');

  sessionExistsCache = null;
  sessionCheckPromise = null;
};

export const resetAuthInterceptorState = () => {
  tokenPromise = null;
  sessionCheckPromise = null;
  sessionExistsCache = null;
};
